// ProjectCardSkeleton.jsx
// Reusable skeleton loader for project cards with shimmer effect

import React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Shimmer overlay component
const ShimmerOverlay = () => (
  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
);

// Single project card skeleton
export const ProjectCardSkeleton = ({ className }) => {
  return (
    <Card className={cn(
      "relative overflow-hidden h-[135px] border-slate-200",
      className
    )}>
      {/* Shimmer effect */}
      <ShimmerOverlay />
      
      {/* Card Layout: 1/3 Image + 2/3 Content */}
      <div className="flex h-full">
        {/* Left: Image placeholder (1/3) */}
        <div className="w-1/3 flex-shrink-0 bg-slate-200 animate-pulse" />
        
        {/* Right: Content placeholder (2/3) */}
        <div className="w-2/3 flex flex-col p-3">
          {/* Title skeleton */}
          <div className="flex items-start gap-2 mb-2">
            <div className="w-4 h-4 rounded bg-slate-200 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
            </div>
          </div>
          
          {/* Address skeleton */}
          <div className="flex items-start gap-2 mb-auto">
            <div className="w-3.5 h-3.5 rounded bg-slate-200 animate-pulse flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="h-3 bg-slate-200 rounded animate-pulse w-full" />
            </div>
          </div>
          
          {/* Footer skeleton */}
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-slate-200 animate-pulse" />
              <div className="h-2.5 bg-slate-200 rounded animate-pulse w-12" />
            </div>
            <div className="h-2.5 bg-slate-200 rounded animate-pulse w-16" />
          </div>
        </div>
      </div>
    </Card>
  );
};

// Grid of skeleton cards
export const ProjectCardSkeletonGrid = ({ count = 6, className }) => {
  return (
    <div className={cn(
      "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
      className
    )}>
      {Array.from({ length: count }).map((_, index) => (
        <ProjectCardSkeleton key={index} />
      ))}
    </div>
  );
};

export default ProjectCardSkeleton;
